
// Backend slider
import  { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiUrl } from '../config/api';
import './Slider.css';

interface SlideItem {
  id: number;
  image: string;           // filename
  image_url: string;       // full URL from API
  title: string;
  buttonText: string;
  buttonLink: string;
  fullDescription?: string;
  date?: string;
  author?: string;
  category?: string;
  impactAreas?: string[];
}

const Slider = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [slides, setSlides] = useState<SlideItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  
  // Fetch slides from API
  useEffect(() => {
    fetchSlides();
  }, []);

  const fetchSlides = async () => {
    try {
      setLoading(true);
      const response = await fetch(apiUrl('/api/slides'));
      
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      
      const result = await response.json();
      
      if (result.success) {
        setSlides(result.data);
        console.log(`Loaded ${result.data.length} slides from database`);
      } else {
        setError('API returned unsuccessful response');
      }
    } catch (err) {
      setError(`Failed to load slides: ${err instanceof Error ? err.message : 'Unknown error'}`);
      console.error('Fetch error:', err);
      
      // Fallback: Use hardcoded data if API fails
      setSlides([
        {
          id: 1,
          image: 'slider1.jpg',
          image_url: '/images/slider/slider1.jpg',
          title: "Six-Month Training Program – 50 Student Graduates Successfully",
          buttonText: "Read More",
          buttonLink: "sustainable-solutions",
          fullDescription: "NORA's Sustainable Solutions program focuses on addressing the dual challenges of Health and humanitarian crises in Afghanistan. Our comprehensive approach combines traditional knowledge with modern technology to create resilient communities. We work directly with local populations to develop education-smart agriculture, disaster preparedness plans, and sustainable water management systems that withstand environmental shocks and stresses.",
          date: "January 15, 2024",
          author: "NORA  Team",
          category: "education Action",
          impactAreas: ["education Resilience", "Disaster Response", "Community Empowerment"]
        },
        // ... other slides
      ]);
    } finally {
      setLoading(false);
    }
  };

  // Auto-slide functionality (only when we have slides)
  useEffect(() => {
    if (slides.length === 0) return;
    
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    
    return () => clearInterval(interval);
  }, [slides.length]);

  const goToSlide = (index: number): void => {
    if (index >= 0 && index < slides.length) {
      setCurrentSlide(index);
    }
  };

  const nextSlide = () => {
    if (slides.length === 0) return;
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    if (slides.length === 0) return;
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const handleSlideClick = (slide: SlideItem) => {
    navigate(`/slider/${slide.id}`, { 
      state: { 
        slideData: slide 
      } 
    });
  };

  // Loading state
  if (loading) {
    return (
      <div className="slider-container">
        <div className="slider-wrapper">
          <div className="slide loading-slide">
            <div className="slide-overlay"></div>
            <div className="slide-content">
              <div className="content-wrapper-column">
                <div className="slide-text-column">
                  <div className="loading-title"></div>
                </div>
                <div className="slide-button-container">
                  <div className="loading-button"></div>
                </div>
              </div>
            </div>
          </div>
          <div className="loading-dots">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="loading-dot"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error && slides.length === 0) {
    return (
      <div className="slider-container">
        <div className="slider-error">
          <p>⚠️ {error}</p>
          <p className="error-help">Slider data unavailable</p>
        </div>
      </div>
    );
  }

  // No slides state
  if (slides.length === 0) {
    return (
      <div className="slider-container">
        <div className="slider-empty">
          <p>No slides available at the moment.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="slider-container">
      {/* Slider Section */}
      <div className="slider-wrapper">
        {slides.map((slide, index) => (
          <div
            key={slide.id}
              className={`slide ${index === currentSlide ? 'active' : ''}`}
              style={{ 
                backgroundImage: `url(${slide.image_url})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}
          >
            <div className="slide-overlay"></div>
            
            <div className="slide-content">
              <div className="content-wrapper-column">
                {/* Column Layout */}
                <div className="slide-text-column">
                  <h2 className="slide-title">{slide.title}</h2>
                </div>
                
                <div className="slide-button-container">
                  <button 
                    className="slide-button"
                    onClick={() => handleSlideClick(slide)}
                  >
                    {slide.buttonText}
                    <i className="fas fa-arrow-right"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
        
        {/* Navigation Arrows */}
        <button className="slider-arrow prev-arrow" onClick={prevSlide}>
          <i className="fas fa-chevron-left"></i>
        </button>
        <button className="slider-arrow next-arrow" onClick={nextSlide}>
          <i className="fas fa-chevron-right"></i>
        </button>
        
        {/* Dots Indicator */}
        <div className="slider-dots">
          {slides.map((_, index) => (
            <button
              key={index}
              className={`dot ${index === currentSlide ? 'active' : ''}`}
              onClick={() => goToSlide(index)}
            />
          ))}
        </div>
      </div>

      
        {/* ADMIN FEATURES FOR LATER:
          When building admin dashboard:

          For impact areas (comma-separated input):
          html
          <input 
            type="text" 
            name="impact_areas" 
            value="{{ old('impact_areas', $slide ? implode(', ', $slide->impact_areas ?? []) : '') }}"
            placeholder="Education, Health, Empowerment"
            class="form-control"
          >
          <p class="help-text">Enter impact areas separated by commas</p>
          In controller store/update:
          php
          // Convert comma-separated string to array
          $validated = $request->validate([
              // ... other fields
              'impact_areas' => 'nullable|string',
          ]);

          if ($request->has('impact_areas')) {
              $impactAreas = array_map('trim', explode(',', $request->impact_areas));
              $validated['impact_areas'] = json_encode($impactAreas);
          }

          $slide->update($validated); */}
    </div>
  );
};

export default Slider;















































//Frontend slider 
// import  { useState, useEffect } from 'react';
// import { useNavigate } from 'react-router-dom';
// import './Slider.css';
// import ittt1 from '../images/slider/slider1.jpg'
// import ittt2 from '../images/slider/slider2.jpg'
// import ittt3 from '../images/slider/slider3.jpg'
// import ittt4 from '../images/slider/slider4.jpg'
// import ittt5 from '../images/slider/slider5.jpg'

// interface SlideItem {
//   id: number;
//   image: string;
//   title: string;
//   buttonText: string;
//   buttonLink: string;
//   fullDescription?: string;
//   date?: string;
//   author?: string;
//   category?: string;
//   impactAreas?: string[];
// }

// const Slider = () => {
//   const [currentSlide, setCurrentSlide] = useState(0);
//   const navigate = useNavigate();
  
//   const slides: SlideItem[] = [
//     {
//       id: 1,
//       image: ittt1,
//       title: "Six-Month Training Program – 50 Student Graduates Successfully",
//       buttonText: "Read More",
//       buttonLink: "sustainable-solutions",
//       fullDescription: "NORA's Sustainable Solutions program focuses on addressing the dual challenges of Health and humanitarian crises in Afghanistan. Our comprehensive approach combines traditional knowledge with modern technology to create resilient communities. We work directly with local populations to develop education-smart agriculture, disaster preparedness plans, and sustainable water management systems that withstand environmental shocks and stresses.",
//       date: "January 15, 2024",
//       author: "NORA  Team",
//       category: "education Action",
//       impactAreas: ["education Resilience", "Disaster Response", "Community Empowerment"]
//     },
//     {
//       id: 2,
//       image: ittt2,
//       title: "Signing a memorandum of understanding (MoU)",
//        buttonText: "Learn More", 
//       buttonLink: "resilient-communities",
//       fullDescription: "Our Building Resilient Communities initiative focuses on creating self-sustaining communities that can withstand economic, environmental, and social challenges. Through capacity building, infrastructure development, and local leadership training, we empower communities to drive their own development. The program includes vocational training, small business development, and community-based disaster risk management that puts local people in charge of their future.",
//       date: "February 20, 2024",
//       author: "NORA Community Development Team",
//       category: "Community Development",
//       impactAreas: ["Economic Empowerment", "Infrastructure", "Local Governance"]
//     },
//     {
//       id: 3,
//       image: ittt3,
//       title: "Scientific Seminar on Emerging Technologies at Wardak University",
//       buttonText: "Get Involved",
//       buttonLink: "education-action",
//       fullDescription: "The education Action & Humanitarian Aid program represents NORA's integrated approach to addressing both immediate needs and long-term sustainability. We combine emergency humanitarian assistance with education adaptation measures, ensuring that communities not only survive crises but emerge stronger. Our work includes reforestation projects, clean energy initiatives, and education education alongside essential humanitarian support like food security and healthcare access.",
//       date: "March 10, 2024",
//       author: "NORA Integrated Programs Team",
//       category: "Integrated Programs", 
//       impactAreas: ["Environmental Conservation", "Humanitarian Aid", "Education"]
//     },
//         {
//       id: 4,
//       image: ittt4,
//       title: "NORA’s Commitment and Achievement",
//      buttonText: "Get Involved",
//       buttonLink: "education-action",
//       fullDescription: "The education Action & Humanitarian Aid program represents NORA's integrated approach to addressing both immediate needs and long-term sustainability. We combine emergency humanitarian assistance with education adaptation measures, ensuring that communities not only survive crises but emerge stronger. Our work includes reforestation projects, clean energy initiatives, and education education alongside essential humanitarian support like food security and healthcare access.",
//       date: "March 10, 2024",
//       author: "NORA Integrated Programs Team",
//       category: "Integrated Programs", 
//       impactAreas: ["Environmental Conservation", "Humanitarian Aid", "Education"]
//     },
//         {
//       id: 5,
//       image: ittt5,
//       title: "NORA Freelancing Training Program. 20 Women Graduates Successfully",
//       buttonText: "Get Involved",
//       buttonLink: "education-action",
//       fullDescription: "The education Action & Humanitarian Aid program represents NORA's integrated approach to addressing both immediate needs and long-term sustainability. We combine emergency humanitarian assistance with education adaptation measures, ensuring that communities not only survive crises but emerge stronger. Our work includes reforestation projects, clean energy initiatives, and education education alongside essential humanitarian support like food security and healthcare access.",
//       date: "March 10, 2024",
//       author: "NORA Integrated Programs Team",
//       category: "Integrated Programs", 
//       impactAreas: ["Environmental Conservation", "Humanitarian Aid", "Education"]
//     }
//   ];

//   // Auto-slide functionality
//   useEffect(() => {
//     const interval = setInterval(() => {
//       setCurrentSlide((prev) => (prev + 1) % slides.length);
//     }, 5000);
    
//     return () => clearInterval(interval);
//   }, [slides.length]);

//   const goToSlide = (index: number): void => {
//     if (index >= 0 && index < slides.length) {
//       setCurrentSlide(index);
//     }
//   };

//   const nextSlide = () => {
//     setCurrentSlide((prev) => (prev + 1) % slides.length);
//   };

//   const prevSlide = () => {
//     setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
//   };

//   const handleSlideClick = (slide: SlideItem) => {
//     // Navigate using the slide ID in the URL
//     navigate(`/slider/${slide.id}`, { 
//       state: { 
//         slideData: slide 
//       } 
//     });
//   };

//   return (
//     <div className="slider-container">
//       {/* Slider Section */}
//       <div className="slider-wrapper">
//         {slides.map((slide, index) => (
//           <div
//             key={slide.id}
//             className={`slide ${index === currentSlide ? 'active' : ''}`}
//             style={{ backgroundImage: `url(${slide.image})` }}
//           >
//             <div className="slide-overlay"></div>
            
//             <div className="slide-content">
//               <div className="content-wrapper-column">
//                 {/* Column Layout */}
//                 <div className="slide-text-column">
//                   <h2 className="slide-title">{slide.title}</h2>
//                 </div>
                
//                 <div className="slide-button-container">
//                   <button 
//                     className="slide-button"
//                     onClick={() => handleSlideClick(slide)}
//                   >
//                     {slide.buttonText}
//                     <i className="fas fa-arrow-right"></i>
//                   </button>
//                 </div>
//               </div>
//             </div>
//           </div>
//         ))}
        
//         {/* Navigation Arrows */}
//         <button className="slider-arrow prev-arrow" onClick={prevSlide}>
//           <i className="fas fa-chevron-left"></i>
//         </button>
//         <button className="slider-arrow next-arrow" onClick={nextSlide}>
//           <i className="fas fa-chevron-right"></i>
//         </button>
        
//         {/* Dots Indicator */}
//         <div className="slider-dots">
//           {slides.map((_, index) => (
//             <button
//               key={index}
//               className={`dot ${index === currentSlide ? 'active' : ''}`}
//               onClick={() => goToSlide(index)}
//             />
//           ))}
//         </div>
//       </div>
//     </div>
//   );
// };

// export default Slider;